import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api, ENDPOINTS } from '../services';
import { toast } from 'react-toastify';
import {FILTER_VALUES, PAGINATION, TOAST_MESSAGES, TICKET_CATEGORIES, TICKET_CATEGORY_NAMES, PROJECT_TYPES, DEFAULT_PRIORITY_ORDER} from '../shared/appConstants';
import { updateProject } from './projectSlice';

const DEFAULT_FILTERS = {
  searchTerm: '',
  selectedProject: FILTER_VALUES.ALL,
  selectedPriority: FILTER_VALUES.ALL,
  selectedAssignee: FILTER_VALUES.ALL,
  selectedCreatedBy: FILTER_VALUES.ALL,
  selectedTaskType: FILTER_VALUES.ALL,
  selectedStatus: FILTER_VALUES.ALL,
  selectedLabels: [FILTER_VALUES.ALL],
  selectedSprint: [FILTER_VALUES.ALL],
  selectedEpic: FILTER_VALUES.ALL,
  startDate: '',
  dueDate: '',
  selectedSort: 'NEWEST_FIRST'
};

const getInitialFilters = () => {
  if (typeof window !== 'undefined') {
    try {
      const saved = localStorage.getItem('dashboardFilterValues');
      if (saved) {
        return { ...DEFAULT_FILTERS, ...JSON.parse(saved) };
      }
    } catch (e) {
      console.error('Error loading filters from localStorage:', e);
    }
  }
  return { ...DEFAULT_FILTERS };
};

const saveFiltersToStorage = (filters) => {
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem('dashboardFilterValues', JSON.stringify(filters));
    } catch (e) {
      console.error('Error saving filters to localStorage:', e);
    }
  }
};

const clearFiltersFromStorage = () => {
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem('dashboardFilterValues');
    } catch (e) {
      console.error('Error clearing filters from localStorage:', e);
    }
  }
};

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);

const isSameTicketId = (left, right) => String(left) === String(right);

const buildTicketMergeData = (updated = {}, argData = {}) => {
  const mergeData = { ...updated };

  if (hasOwn(argData, 'startDate')) {
    mergeData.start_date = argData.startDate;
    mergeData.startDate = argData.startDate;
  }
  if (hasOwn(argData, 'start_date')) {
    mergeData.start_date = argData.start_date;
    mergeData.startDate = argData.start_date;
  }
  if (hasOwn(argData, 'dueDate')) {
    mergeData.end_date = argData.dueDate;
    mergeData.due_date = argData.dueDate;
    mergeData.dueDate = argData.dueDate;
  }
  if (hasOwn(argData, 'due_date')) {
    mergeData.end_date = argData.due_date;
    mergeData.due_date = argData.due_date;
    mergeData.dueDate = argData.due_date;
  }
  if (hasOwn(argData, 'has_due_time')) {
    // Keep both API and UI shapes in sync during optimistic updates.
    mergeData.has_due_time = Boolean(argData.has_due_time);
    mergeData.hasDueTime = Boolean(argData.has_due_time);
  }
  if (hasOwn(argData, 'marked_as_pin')) {
    mergeData.marked_as_pin = argData.marked_as_pin;
  }

  return mergeData;
};

const mergeTicketRecursively = (tickets = [], updatedId, mergeData) => {
  let didChange = false;

  const nextTickets = tickets.map((ticket) => {
    if (isSameTicketId(ticket.id, updatedId)) {
      didChange = true;
      return {
        ...ticket,
        ...mergeData
      };
    }

    if (Array.isArray(ticket.subtasks) && ticket.subtasks.length > 0) {
      const nextSubtasks = mergeTicketRecursively(ticket.subtasks, updatedId, mergeData);
      if (nextSubtasks !== ticket.subtasks) {
        didChange = true;
        return {
          ...ticket,
          subtasks: nextSubtasks
        };
      }
    }

    return ticket;
  });

  return didChange ? nextTickets : tickets;
};

const sortTicketsWithPinnedFirst = (tickets = []) => {
  if (!Array.isArray(tickets) || tickets.length === 0) return tickets;

  const indexedTickets = tickets.map((ticket, index) => ({
    ticket: {
      ...ticket,
      subtasks: Array.isArray(ticket.subtasks)
        ? sortTicketsWithPinnedFirst(ticket.subtasks)
        : ticket.subtasks
    },
    index
  }));

  indexedTickets.sort((left, right) => {
    const leftPinned = Boolean(left.ticket?.marked_as_pin);
    const rightPinned = Boolean(right.ticket?.marked_as_pin);

    if (leftPinned !== rightPinned) {
      return leftPinned ? -1 : 1;
    }

    return left.index - right.index;
  });

  return indexedTickets.map(({ ticket }) => ticket);
};

const applyTicketMergeToState = (state, updatedId, mergeData) => {
  if (!updatedId) return;

  if (state.ticketDetails && isSameTicketId(state.ticketDetails.id, updatedId)) {
    state.ticketDetails = { ...state.ticketDetails, ...mergeData };
  }

  Object.keys(state.tickets).forEach((statusName) => {
    if (state.tickets[statusName] && Array.isArray(state.tickets[statusName].tickets)) {
      state.tickets[statusName].tickets = mergeTicketRecursively(
        state.tickets[statusName].tickets,
        updatedId,
        mergeData
      );

      if (hasOwn(mergeData, 'marked_as_pin')) {
        state.tickets[statusName].tickets = sortTicketsWithPinnedFirst(state.tickets[statusName].tickets);
      }
    }
  });
};

export const fetchTicketDropdowns = createAsyncThunk('tickets/fetchTicketDropdowns', async (project_id) => {
  const url = project_id ? `${ENDPOINTS.TICKETS_DROPDOWNS}?project_id=${project_id}` : ENDPOINTS.TICKETS_DROPDOWNS;
  // Keep the existing dropdown response untouched, and fetch Scrum sprints separately
  // because the Scrum sprint filter needs completed sprints while legacy sprint edit
  // flows should continue to receive only active sprint values.
  const [response, scrumSprintResponse] = await Promise.all([
    api.get(url),
    project_id
      ? api.get(`${ENDPOINTS.TICKETS_DROPDOWNS}?project_id=${project_id}&type=sprint&include_completed=true`)
      : Promise.resolve({ data: { data: [] } })
  ]);
  if (!response?.data?.data || typeof response.data.data !== 'object') {
    throw new Error('Invalid response format');
  }
  return {
    ...response.data.data,
    scrum_sprints: Array.isArray(scrumSprintResponse?.data?.data) ? scrumSprintResponse.data.data : []
  };
});

export const fetchProjectDropdownsByUser = createAsyncThunk('tickets/fetchProjectDropdownsByUser', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get(ENDPOINTS.PROJECT_DROPDOWNS_BY_USER);
    return response.data?.data || [];
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    return rejectWithValue(message);
  }
});

export const fetchLastSelectedProject = createAsyncThunk('tickets/fetchLastSelectedProject', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get(ENDPOINTS.USER_PREFERENCES('last_selected_project'));
    return response.data?.data?.last_selected_project?.project_id || null;
  } catch (error) {
    // Return null if preference not found, don't treat as error
    return null;
  }
});

export const saveUserPreference = createAsyncThunk('tickets/saveUserPreference', async ({ preferenceKey, preferenceValue }, { rejectWithValue }) => {
  try {
    const body = {
      preference_key: preferenceKey,
      preference_value: preferenceValue
    };
    await api.post(ENDPOINTS.SAVE_USER_PREFERENCES, body);
    return { preferenceKey, preferenceValue };
  } catch (error) {
    // Don't show error toast for preference save failures
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

export const fetchAllTickets = createAsyncThunk('tickets/fetchAllTickets', async (ticketId, { rejectWithValue }) => {
  try {
    const response = await api.get(ENDPOINTS.TICKETS_DETAILS(ticketId));
    return response.data?.data || {};
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    return rejectWithValue(message);
  }
});

export const fetchTicketsByProject = createAsyncThunk('tickets/fetchTicketsByProject', async ({ projectId, limit = PAGINATION.DEFAULT_LIMIT, page = PAGINATION.DEFAULT_PAGE, filters = {}, append = false }, { rejectWithValue }) => {
  try {
    const params = new URLSearchParams({ limit, page });
    
    // Add filter parameters
    if (filters.search) params.append('search', filters.search);
    if (filters.priority && filters.priority !== FILTER_VALUES.ALL) params.append('priority', filters.priority);
    if (filters.status && filters.status !== FILTER_VALUES.ALL) params.append('status', filters.status);
    if (filters.assignee && filters.assignee !== FILTER_VALUES.ALL) params.append('assignee', filters.assignee);
    if (filters.created_by && filters.created_by !== FILTER_VALUES.ALL) params.append('created_by', filters.created_by);
    if (filters.taskType && filters.taskType !== FILTER_VALUES.ALL) params.append('type', filters.taskType);
    if (filters.labels && !filters.labels.includes(FILTER_VALUES.ALL)) params.append('labels', filters.labels.join(','));
    if (filters.sprint && !filters.sprint.includes(FILTER_VALUES.ALL)) params.append('sprint', filters.sprint.join(','));
    // Scrum projects use sprint_details via scrum_sprint_id; Kanban keeps the legacy sprint filter.
    if (filters.scrum_sprint_id && String(filters.scrum_sprint_id).trim()) {
      params.append('scrum_sprint_id', String(filters.scrum_sprint_id).trim());
    }
    if (filters.epic && filters.epic !== FILTER_VALUES.ALL) params.append('epic', filters.epic);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.due_date) params.append('due_date', filters.due_date);
    if (filters.sort) params.append('sort', filters.sort);
    
    const response = await api.get(`${ENDPOINTS.TICKETS_BY_PROJECT(projectId)}?${params.toString()}`);
    return { data: response.data?.data || {}, append, page };
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    return rejectWithValue(message);
  }
});

const isTicketInScrumProject = (state, ticketId) => {
  if (state?.tickets?.ticketDetails?.id === ticketId) {
    return (state.tickets.ticketDetails?.project?.project_type || '').toUpperCase() === PROJECT_TYPES.SCRUM.toUpperCase();
  }
  
  const ticketsData = state?.tickets?.tickets;
  if (!ticketsData || typeof ticketsData !== 'object') {
    return false;
  }
  
  const allTickets = Object.values(ticketsData).flatMap(t => (t && typeof t === 'object' ? t.tickets || [] : []));
  const ticket = allTickets.find(t => t?.id === ticketId);
  return (ticket?.project?.project_type || '').toUpperCase() === PROJECT_TYPES.SCRUM.toUpperCase() || false;
};

export const fetchTicketsByColumn = createAsyncThunk('tickets/fetchTicketsByColumn', async ({ projectId, statusId, limit = PAGINATION.DEFAULT_LIMIT, page = PAGINATION.DEFAULT_PAGE, filters = {} }, { rejectWithValue }) => {
  try {
    const params = new URLSearchParams({ limit, page, status: statusId });
    
    // Add filter parameters
    if (filters.search) params.append('search', filters.search);
    if (filters.priority && filters.priority !== FILTER_VALUES.ALL) params.append('priority', filters.priority);
    if (filters.assignee && filters.assignee !== FILTER_VALUES.ALL) params.append('assignee', filters.assignee);
    if (filters.created_by && filters.created_by !== FILTER_VALUES.ALL) params.append('created_by', filters.created_by);
    if (filters.taskType && filters.taskType !== FILTER_VALUES.ALL) params.append('type', filters.taskType);
    if (filters.labels && !filters.labels.includes(FILTER_VALUES.ALL)) params.append('labels', filters.labels.join(','));
    if (filters.sprint && !filters.sprint.includes(FILTER_VALUES.ALL)) params.append('sprint', filters.sprint.join(','));
    // Scrum projects use sprint_details via scrum_sprint_id; Kanban keeps the legacy sprint filter.
    if (filters.scrum_sprint_id && String(filters.scrum_sprint_id).trim()) {
      params.append('scrum_sprint_id', String(filters.scrum_sprint_id).trim());
    }
    if (filters.epic && filters.epic !== FILTER_VALUES.ALL) params.append('epic', filters.epic);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.due_date) params.append('due_date', filters.due_date);
    if (filters.sort) params.append('sort', filters.sort);
    
    const response = await api.get(`${ENDPOINTS.TICKETS_BY_PROJECT(projectId)}?${params.toString()}`);
    return { data: response.data?.data || {}, statusId, page };
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    return rejectWithValue(message);
  }
});

const buildTicketPayload = (ticketData, getState, { includeAttachments = false } = {}) => {
  const body = {
    title: ticketData.title,
    type_id: ticketData.taskType,
    priority_id: ticketData.priority,
    status_id: ticketData.status,
    project_id: ticketData.project,
    due_date: ticketData.dueDate || null,
    has_due_time: Boolean(ticketData.has_due_time),
    start_date: ticketData.startDate || null,
    description: ticketData.description || '',
    assignee: Array.isArray(ticketData.assignees) && ticketData.assignees.length > 0
      ? ticketData.assignees
      : (ticketData.assignee ? [ticketData.assignee] : []),
    labels: ticketData.labels || [],
    parent_ticket_id: ticketData.parent_ticket_id || null,
    acceptance_criteria: ticketData.acceptanceCriteria || '',
    is_recurring: Boolean(ticketData.is_recurring),
    recurrence_config: ticketData.is_recurring ? (ticketData.recurrence_config || null) : null
  };
  // Existing tickets send attachment deltas; only ticket creation sends a full array.
  if (includeAttachments) body.attachments = ticketData.attachments || [];
  if (ticketData.attachment_operations) body.attachment_operations = ticketData.attachment_operations;

  // A ticket edit that did not touch the checklist must not turn an omitted
  // field into an empty checklist update.
  if (Object.prototype.hasOwnProperty.call(ticketData, 'checklist')) {
    body.checklist = ticketData.checklist || [];
  }

  if (ticketData.marked_as_pin !== undefined) {
    body.marked_as_pin = ticketData.marked_as_pin;
  }

  let isScrum = ticketData.story_points !== undefined;
  if (!isScrum && ticketData.project && getState) {
    const state = getState();
    const allProjects = Object.values(state.projects?.projects || {}).flat();
    const project = allProjects.find(p => p.id === ticketData.project);
    if (project && (project.project_type || '').toUpperCase() === PROJECT_TYPES.SCRUM.toUpperCase()) {
      isScrum = true;
    }
  }

  if (isScrum) {
    body.scrum_sprint_id = ticketData.sprint || null;
  } else {
    body.sprint_id = ticketData.sprint || null;
  }

  body.estimate_time_in_minutes = ticketData.estimate_time_in_minutes !== undefined ? ticketData.estimate_time_in_minutes : 0;
  if (ticketData.story_points !== undefined) {
    body.story_points = ticketData.story_points;
  }

  return body;
};
export const createTicket = createAsyncThunk('tickets/createTicket', async (ticketData, { getState, rejectWithValue }) => {
  try {
    const body = buildTicketPayload(ticketData, getState, { includeAttachments: true });
    const response = await api.post(ENDPOINTS.TICKETS, body);
    toast.success('Ticket created successfully!');
    return { ...response.data?.data, projectId: ticketData.project };
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    toast.error(`Failed to create ticket: ${message}`);
    return rejectWithValue(message);
  }
});

export const updateTicket = createAsyncThunk('tickets/updateTicket', async ({ ticketId, ticketData }, { getState, rejectWithValue }) => {
  try {
    if (!ticketId) {
      throw new Error('Ticket ID is required for update');
    }
    const body = buildTicketPayload(ticketData, getState);
    const response = await api.put(ENDPOINTS.TICKETS_UPDATE(ticketId), body);
    toast.success('Ticket updated successfully!');
    return { ...response.data?.data, projectId: ticketData.project };
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    toast.error(`Failed to update ticket: ${message}`);
    return rejectWithValue(message);
  }
});

export const updateTicketPartial = createAsyncThunk('tickets/updateTicketPartial', async ({ ticketId, ticketData }, { getState, rejectWithValue }) => {
  try {
    if (!ticketId) {
      throw new Error('Ticket ID is required for update');
    }
    const processedData = { ...ticketData };
    if ('due_date' in processedData && processedData.due_date === '') {
      processedData.due_date = null;
    }
    
    if ('sprint_id' in processedData) {
      const state = getState();
      const isScrum = isTicketInScrumProject(state, ticketId);
      
      if (isScrum) {
         processedData.scrum_sprint_id = processedData.sprint_id;
         delete processedData.sprint_id;
      }
    }
    
    const response = await api.put(ENDPOINTS.TICKETS_UPDATE(ticketId), processedData);
    return response.data?.data;
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    return rejectWithValue(message);
  }
});

export const updateTicketAttachments = createAsyncThunk('tickets/updateTicketAttachments', async ({ ticketId, add = [], removeKeys = [], rename = [] }, { rejectWithValue }) => {
  try {
    if (!ticketId) throw new Error('Ticket ID is required for attachment update');
    const response = await api.put(ENDPOINTS.TICKETS_UPDATE(ticketId), {
      attachment_operations: {
        ...(add.length ? { add } : {}),
        ...(removeKeys.length ? { remove_keys: removeKeys } : {}),
        ...(rename.length ? { rename } : {})
      }
    });
    // Use the authoritative merge returned by the server, including concurrent additions.
    return response.data?.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.error || error.response?.data?.message || error.message);
  }
});

export const deleteTicket = createAsyncThunk('tickets/deleteTicket', async (ticketId, { rejectWithValue }) => {
  try {
    if (!ticketId) {
      throw new Error('Ticket ID is required for delete');
    }
    await api.delete(ENDPOINTS.TICKETS_DELETE(ticketId));
    toast.success('Ticket deleted successfully!');
    return ticketId;
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    toast.error(`Failed to delete ticket: ${message}`);
    return rejectWithValue(message);
  }
});

export const fetchTicketComments = createAsyncThunk('tickets/fetchTicketComments', async (ticketId, { rejectWithValue }) => {
  try {
    if (!ticketId) {
      throw new Error('Ticket ID is required to fetch comments');
    }
    const response = await api.get(ENDPOINTS.TICKETS_COMMENTS(ticketId));
    return response.data?.data || [];
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    return rejectWithValue(message);
  }
});

export const createTicketComment = createAsyncThunk('tickets/createTicketComment', async ({ ticketId, comment, attachments = [], parentCommentId = undefined, suppressToast = false }, { rejectWithValue }) => {
  try {
    if (!ticketId) {
      throw new Error('Ticket ID is required for comment');
    }
    const body = { comment, attachments };
    if (parentCommentId !== undefined) {
      body.parent_comment_id = parentCommentId;
    }
    const response = await api.post(ENDPOINTS.TICKETS_COMMENTS(ticketId), body);
    if (!suppressToast) {
      toast.success('Comment added successfully!');
    }
    return response.data?.data || null;
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    if (!suppressToast) {
      toast.error(`Failed to add comment: ${message}`);
    }
    return rejectWithValue(message);
  }
});

export const updateComment = createAsyncThunk('tickets/updateComment', async ({ commentId, comment, attachments = [] }, { rejectWithValue }) => {
  try {
    if (!commentId) {
      throw new Error('Comment ID is required for update');
    }
    const body = { comment, attachments };
    await api.put(ENDPOINTS.COMMENTS_UPDATE(commentId), body);
    toast.success('Comment updated successfully!');
    return { commentId, comment, attachments };
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    toast.error(`Failed to update comment: ${message}`);
    return rejectWithValue(message);
  }
});

export const deleteComment = createAsyncThunk('tickets/deleteComment', async (commentId, { rejectWithValue }) => {
  try {
    if (!commentId) {
      throw new Error('Comment ID is required for delete');
    }
    await api.delete(ENDPOINTS.COMMENTS_DELETE(commentId));
    toast.success('Comment deleted successfully!');
    return commentId;
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    toast.error(`Failed to delete comment: ${message}`);
    return rejectWithValue(message);
  }
});

// Helper function to convert time format (2h 30m) to minutes
const convertTimeToMinutes = (timeString) => {
  if (!timeString) return 0;
  
  let totalMinutes = 0;
  const hourMatch = timeString.match(/(\d+)h/i);
  const minuteMatch = timeString.match(/(\d+)m/i);
  
  if (hourMatch) {
    totalMinutes += parseInt(hourMatch[1]) * 60;
  }
  if (minuteMatch) {
    totalMinutes += parseInt(minuteMatch[1]);
  }
  
  return totalMinutes;
};

export const createWorklog = createAsyncThunk('tickets/createWorklog', async ({ ticketId, worklogData }, { rejectWithValue }) => {
  try {
    if (!ticketId) {
      throw new Error('Ticket ID is required for worklog');
    }
    const body = {
      log_date: worklogData.log_date,
      description: worklogData.description,
      time_spent: convertTimeToMinutes(worklogData.time_spent)
    };
    const response = await api.post(ENDPOINTS.TICKETS_WORKLOGS(ticketId), body);
    toast.success('Worklog added successfully!');
    return response.data?.data || null;
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    toast.error(`Failed to add worklog: ${message}`);
    return rejectWithValue(message);
  }
});

export const updateWorklog = createAsyncThunk('tickets/updateWorklog', async ({ worklogId, worklogData }, { rejectWithValue }) => {
  try {
    if (!worklogId) {
      throw new Error('Worklog ID is required for update');
    }
    const body = {
      log_date: worklogData.log_date,
      description: worklogData.description,
      time_spent: convertTimeToMinutes(worklogData.time_spent)
    };
    const response = await api.put(ENDPOINTS.TICKETS_WORKLOG_UPDATE(worklogId), body);
    toast.success('Worklog updated successfully!');
    return response.data?.data || null;
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    toast.error(`Failed to update worklog: ${message}`);
    return rejectWithValue(message);
  }
});

export const deleteWorklog = createAsyncThunk('tickets/deleteWorklog', async (worklogId, { rejectWithValue }) => {
  try {
    if (!worklogId) {
      throw new Error('Worklog ID is required for delete');
    }
    await api.delete(ENDPOINTS.TICKETS_WORKLOG_DELETE(worklogId));
    toast.success('Worklog deleted successfully!');
    return worklogId;
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    toast.error(`Failed to delete worklog: ${message}`);
    return rejectWithValue(message);
  }
});

export const fetchWorklogSummary = createAsyncThunk('tickets/fetchWorklogSummary', async (ticketId, { rejectWithValue }) => {
  try {
    if (!ticketId) {
      throw new Error('Ticket ID is required to fetch worklog summary');
    }
    const response = await api.get(ENDPOINTS.TICKETS_SUMMARY(ticketId));
    return response.data?.data || null;
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    return rejectWithValue(message);
  }
});

export const fetchTicketHistory = createAsyncThunk('tickets/fetchTicketHistory', async (ticketId, { rejectWithValue }) => {
  try {
    if (!ticketId) {
      throw new Error('Ticket ID is required to fetch ticket history');
    }
    const response = await api.get(ENDPOINTS.TICKETS_HISTORY(ticketId));
    return response.data?.data || [];
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    return rejectWithValue(message);
  }
});

export const fetchTicketIdByNumber = createAsyncThunk('tickets/fetchTicketIdByNumber', async (ticketNumber, { rejectWithValue }) => {
  try {
    if (!ticketNumber) {
      throw new Error('Ticket number is required to fetch ticket ID');
    }
    const response = await api.get(ENDPOINTS.TICKETS_ID_BY_NUMBER(ticketNumber));
    return response.data?.data || null;
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    return rejectWithValue(message);
  }
});

export const fetchTicketDetails = createAsyncThunk('tickets/fetchTicketDetails', async (ticketId, { rejectWithValue }) => {
  try {
    if (!ticketId) {
      throw new Error('Ticket ID is required to fetch ticket details');
    }
    const response = await api.get(ENDPOINTS.TICKETS_DETAILS(ticketId));
    return response.data?.data || null;
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    return rejectWithValue(message);
  }
});

export const fetchSubtasks = createAsyncThunk('tickets/fetchSubtasks', async (ticketId, { rejectWithValue }) => {
  try {
    if (!ticketId) {
      throw new Error('Ticket ID is required to fetch subtasks');
    }
    const response = await api.get(ENDPOINTS.TICKETS_SUBTASKS(ticketId));
    return response.data?.data || [];
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    return rejectWithValue(message);
  }
});

export const createTicketType = createAsyncThunk('tickets/createTicketType', async ({ name, projectId = '' }, { rejectWithValue }) => {
  try {
    const body = { name, category: 'type', project_id: projectId || null };
    const response = await api.post(ENDPOINTS.TICKETS_DROPDOWNS_CREATE, body);
    toast.success('Task type created successfully!');
    return response.data?.data || null;
  } catch (error) {
    const apiError = error.response?.data?.error || error.message;
    // If API returns "Validation error", show custom message
    const errorMessage = apiError === 'Validation error' 
      ? TOAST_MESSAGES.TASK_TYPE_NAME_EXISTS 
      : (apiError || TOAST_MESSAGES.TASK_TYPE_CREATE_FAILED);
    return rejectWithValue(errorMessage);
  }
});

export const createTicketPriority = createAsyncThunk('tickets/createTicketPriority', async ({ name, colour, projectId = '' }, { rejectWithValue }) => {
  try {
    const body = { name, category: 'priority', project_id: projectId || null };
    if (colour) body.colour = colour;
    const response = await api.post(ENDPOINTS.TICKETS_DROPDOWNS_CREATE, body);
    toast.success('Priority created successfully!');
    return response.data?.data || null;
  } catch (error) {
    const apiError = error.response?.data?.error || error.message;
    // If API returns "Validation error", show custom message
    const errorMessage = apiError === 'Validation error' 
      ? TOAST_MESSAGES.PRIORITY_NAME_EXISTS 
      : (apiError || TOAST_MESSAGES.PRIORITY_CREATE_FAILED);
    return rejectWithValue(errorMessage);
  }
});

export const createSprintPriority = createAsyncThunk('tickets/createSprintPriority', async ({ name, colour, projectId = '' }, { rejectWithValue }) => {
  try {
    const body = { name, category: 'sprint_priority', project_id: projectId || null };
    if (colour) body.colour = colour;
    const response = await api.post(ENDPOINTS.TICKETS_DROPDOWNS_CREATE, body);
    toast.success('Sprint Priority created successfully!');
    return response.data?.data || null;
  } catch (error) {
    const apiError = error.response?.data?.error || error.message;
    const errorMessage = apiError === 'Validation error' 
      ? TOAST_MESSAGES.PRIORITY_NAME_EXISTS 
      : (apiError || TOAST_MESSAGES.PRIORITY_CREATE_FAILED);
    return rejectWithValue(errorMessage);
  }
});

export const createTicketLabel = createAsyncThunk('tickets/createTicketLabel', async ({ name, colour, projectId = '' }, { rejectWithValue }) => {
  try {
    const body = { name, colour, category: 'labels', project_id: projectId || null };
    const response = await api.post(ENDPOINTS.TICKETS_DROPDOWNS_CREATE, body);
    toast.success('Label created successfully!');
    return response.data?.data || null;
  } catch (error) {
    const apiError = error.response?.data?.error || error.message;
    // If API returns "Validation error", show custom message
    const errorMessage = apiError === 'Validation error' 
      ? TOAST_MESSAGES.LABEL_NAME_EXISTS 
      : (apiError || TOAST_MESSAGES.LABEL_CREATE_FAILED);
    return rejectWithValue(errorMessage);
  }
});

export const createTicketSprint = createAsyncThunk('tickets/createTicketSprint', async ({ name, colour, projectId = '' }, { rejectWithValue }) => {
  try {
    const body = { name, category: 'sprint', project_id: projectId || null };
    if (colour) body.colour = colour;
    const response = await api.post(ENDPOINTS.TICKETS_DROPDOWNS_CREATE, body);
    toast.success('Sprint created successfully!');
    return response.data?.data || null;
  } catch (error) {
    const apiError = error.response?.data?.error || error.message;
    // If API returns "Validation error", show custom message
    const errorMessage = apiError === 'Validation error' 
      ? TOAST_MESSAGES.SPRINT_NAME_EXISTS 
      : (apiError || TOAST_MESSAGES.SPRINT_CREATE_FAILED);
    return rejectWithValue(errorMessage);
  }
});

export const createTicketStatus = createAsyncThunk('tickets/createTicketStatus', async ({ name, projectId = '', colour = '' }, { rejectWithValue }) => {
  try {
    const body = { name, category: 'status', project_id: projectId || null };
    if (colour) body.colour = colour;
    const response = await api.post(ENDPOINTS.TICKETS_DROPDOWNS_CREATE, body);
    toast.success('Status created successfully!');
    return response.data?.data || null;
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    toast.error(`Failed to create status: ${message}`);
    return rejectWithValue(message);
  }
});

export const updateTicketDropdown = createAsyncThunk('tickets/updateTicketDropdown', async ({ id, name, category, colour, projectId = '' }, { rejectWithValue }) => {
  try {
    const body = { name, category, project_id: projectId || null };
    if (colour) body.colour = colour;
    const response = await api.post(ENDPOINTS.TICKETS_DROPDOWNS_UPDATE(id), body);
    const categoryName = TICKET_CATEGORY_NAMES[category] || 'Status';
    toast.success(`${categoryName} updated successfully!`);
    return { id, name, category, colour, ...response.data?.data };
  } catch (error) {
    const apiError = error.response?.data?.error || error.message;
    // If API returns "Validation error", show custom message based on category
    let errorMessage;
    if (apiError === 'Validation error') {
      if (category === TICKET_CATEGORIES.LABELS) {
        errorMessage = TOAST_MESSAGES.LABEL_NAME_EXISTS;
      } else if (category === TICKET_CATEGORIES.PRIORITY) {
        errorMessage = TOAST_MESSAGES.PRIORITY_NAME_EXISTS;
      } else if (category === TICKET_CATEGORIES.TYPE) {
        errorMessage = TOAST_MESSAGES.TASK_TYPE_NAME_EXISTS;
      } else if (category === TICKET_CATEGORIES.SPRINT) {
        errorMessage = TOAST_MESSAGES.SPRINT_NAME_EXISTS;
      } else {
        errorMessage = apiError;
      }
    } else {
      if (category === TICKET_CATEGORIES.LABELS) {
        errorMessage = apiError || TOAST_MESSAGES.LABEL_UPDATE_FAILED;
      } else if (category === TICKET_CATEGORIES.PRIORITY) {
        errorMessage = apiError || TOAST_MESSAGES.PRIORITY_UPDATE_FAILED;
      } else if (category === TICKET_CATEGORIES.TYPE) {
        errorMessage = apiError || TOAST_MESSAGES.TASK_TYPE_UPDATE_FAILED;
      } else if (category === TICKET_CATEGORIES.SPRINT) {
        errorMessage = apiError || TOAST_MESSAGES.SPRINT_UPDATE_FAILED;
      } else {
        errorMessage = apiError;
      }
    }
    return rejectWithValue(errorMessage);
  }
});

export const deleteTicketDropdown = createAsyncThunk('tickets/deleteTicketDropdown', async ({ id, category }, { rejectWithValue }) => {
  try {
    await api.delete(ENDPOINTS.TICKETS_DROPDOWNS_DELETE(id));
    const categoryName = TICKET_CATEGORY_NAMES[category] || 'Status';
    toast.success(`${categoryName} deleted successfully!`);
    return { id, category };
  } catch (error) {
    const errorMessage = error.response?.data?.error || error.message || TOAST_MESSAGES.TICKET_ATTRIBUTE_DELETE_FAILED;
    return rejectWithValue(errorMessage);
  }
});

export const updateStatusOrder = createAsyncThunk('tickets/updateStatusOrder', async ({ projectId, statusOrder }, { rejectWithValue }) => {
  try {
    await api.put(ENDPOINTS.PROJECT_STATUS_ORDER(projectId), { status_order: statusOrder });
    return { projectId, statusOrder };
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    return rejectWithValue(message);
  }
});

export const bulkUpdateTickets = createAsyncThunk('tickets/bulkUpdateTickets', async ({ projectId, taskIds, updates }, { rejectWithValue }) => {
  try {
    const categories = [];
    const payload = {};
    
    if (updates.status) {
      categories.push('change_status');
      payload.status_id = updates.status;
    }
    if (updates.assignee) {
      categories.push('assign');
      payload.assignee = [updates.assignee];
    }
    if (updates.dueDate) {
      categories.push('change_due_date');
      payload.due_date = updates.dueDate;
      // Bulk date changes are date-only, so clear any prior due-time semantics.
      payload.has_due_time = false;
    }
    if (updates.comments) {
      categories.push('add_comment');
      payload.comment = updates.comments;
    }
    if (updates.sprint !== undefined) {
      categories.push('change_sprint');
      payload.scrum_sprint_id = updates.sprint === 'backlog' ? null : updates.sprint;
    }
    
    const body = {
      category: categories.join(','),
      task_ids: taskIds,
      payload
    };
    
    const response = await api.put(ENDPOINTS.TICKETS_BULK_ACTIONS(projectId), body);
    const data = response.data?.data;
    
    return { taskIds, updates, response: data };
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    return rejectWithValue(message);
  }
});

export const bulkDeleteTickets = createAsyncThunk('tickets/bulkDeleteTickets', async ({ projectId, taskIds }, { rejectWithValue }) => {
  try {
    const body = {
      category: 'delete',
      task_ids: taskIds,
      payload: {}
    };
    
    const response = await api.put(ENDPOINTS.TICKETS_BULK_ACTIONS(projectId), body);
    const data = response.data?.data;
    
    return { taskIds, response: data };
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    return rejectWithValue(message);
  }
});

export const linkTickets = createAsyncThunk('tickets/linkTickets', async ({ projectId, parentTicketId, relationshipType, targetTicketIds }, { rejectWithValue }) => {
  try {
    const body = {
      rel: relationshipType,
      tk_id: parentTicketId,
      link_tk: targetTicketIds || []
    };
    
    const response = await api.post(ENDPOINTS.LINK_TICKETS(projectId), body);
    toast.success(`Successfully linked ${targetTicketIds?.length || 0} ticket(s)`);
    return response.data?.data;
  } catch (error) {
    const message = error.response?.data?.error || error.response?.data?.message || 'Failed to create link';
    toast.error(message);
    return rejectWithValue(message);
  }
});

export const updateLinkTickets = createAsyncThunk('tickets/updateLinkTickets', async ({ relationshipId, relationshipType, related_ticket_id, target_ticket }, { rejectWithValue }) => {
  try {
    const body = {
      rel: relationshipType,
      related_ticket_id: related_ticket_id,
      target_ticket: target_ticket
    };
    
    const response = await api.put(ENDPOINTS.UPDATE_LINK_TICKETS(relationshipId), body);
    toast.success('Link updated successfully');
    return response.data?.data;
  } catch (error) {
    const message = error.response?.data?.error || error.response?.data?.message || 'Failed to update link';
    toast.error(message);
    return rejectWithValue(message);
  }
});

export const deleteLinkTickets = createAsyncThunk('tickets/deleteLinkTickets', async (relationshipId, { rejectWithValue }) => {
  try {
    await api.delete(ENDPOINTS.DELETE_LINK_TICKETS(relationshipId));
    toast.success('Link deleted successfully');
    return relationshipId;
  } catch (error) {
    const message = error.response?.data?.error || error.response?.data?.message || 'Failed to delete link';
    toast.error(message);
    return rejectWithValue(message);
  }
});

export const searchTickets = createAsyncThunk('tickets/searchTickets', async ({ projectId, searchTerm, task_type }, { rejectWithValue }) => {
  try {
    if (!projectId) return [];
    
    const params = new URLSearchParams();
    if (searchTerm && searchTerm.trim()) {
      params.append('search', searchTerm.trim());
    }
    if (task_type) {
      params.append('task_type', task_type);
    }
    
    const url = params.toString() 
      ? `${ENDPOINTS.SEARCH_TICKETS(projectId)}?${params.toString()}`
      : ENDPOINTS.SEARCH_TICKETS(projectId);
    
    const response = await api.get(url);
    return response.data?.data || [];
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    return rejectWithValue(message);
  }
});

export const searchSprints = createAsyncThunk('tickets/searchSprints', async ({ projectId, searchTerm, limit = 10 }, { rejectWithValue }) => {
  try {
    if (!projectId) return [];
    
    const params = new URLSearchParams();
    if (searchTerm && searchTerm.trim()) {
      params.append('search', searchTerm.trim());
    }
    if (limit) {
      params.append('limit', limit);
    }
    
    const url = params.toString() ? `${ENDPOINTS.SEARCH_SPRINTS(projectId)}?${params.toString()}` : ENDPOINTS.SEARCH_SPRINTS(projectId);
    const response = await api.get(url);
    return response.data?.data || [];
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    return rejectWithValue(message);
  }
});
const sortPriorities = (priorities) => {
  if (!Array.isArray(priorities)) return [];
  return [...priorities].sort((a, b) => {
    const indexA = DEFAULT_PRIORITY_ORDER.indexOf(a.name);
    const indexB = DEFAULT_PRIORITY_ORDER.indexOf(b.name);
    
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    
    return (a.name || '').localeCompare(b.name || '');
  });
};

const ticketSlice = createSlice({
  name: 'tickets',
  initialState: {
    dropdowns: {
      types: [],
      priorities: [],
      statuses: [],
      projects: [],
      assignees: [],
      relationship_types: [],
      sprints: [],
      scrum_sprints: [],
      labels: [],
      epics: [],
      sprint_priorities: []
    },
    projectDropdownsByUser: [],
    projectDropdownsByUserLoading: false,
    projectDropdownsByUserError: null,
    lastSelectedProjectId: null,
    tickets: {},
    ticketsLoading: false,
    latestTicketsRequestId: null,
    dropdownsLoading: false,
    currentPage: PAGINATION.DEFAULT_PAGE,
    hasMoreTickets: true,
    columnPagination: {}, // Track pagination per column: { statusId: { page, hasMore, loading } }
    comments: [],
    commentsLoading: false,
    worklogSummary: null,
    worklogSummaryLoading: false,
    ticketHistory: [],
    ticketHistoryLoading: false,
    ticketDetails: null,
    ticketDetailsLoading: false,
    ticketIdByNumber: null,
    ticketIdByNumberLoading: false,
    lastLinkUpdateAt: null,
    lastSubtaskUpdateAt: null,
    draggedStatus: null,
    dragOverStatus: null,
    previousStatusOrder: null,
    error: null,
    lastCreated: null,
    allTickets: {}, // Added for fetching all tickets
    searchResults: [],
    searchLoading: false,
    searchSprintsResults: [],
    searchSprintsLoading: false,
    filters: getInitialFilters()
  },
  reducers: {
    clearTickets: (state) => {
      // Only clear if explicitly requested, not on navigation
      state.tickets = {};
      state.columnPagination = {};
    },
    clearTicketDetails: (state) => {
      state.ticketDetails = null;
      state.comments = [];
      state.worklogSummary = null;
      state.ticketHistory = [];
    },
    clearError: (state) => {
      state.error = null;
    },
    markSubtaskUpdated: (state) => {
      state.lastSubtaskUpdateAt = (state.lastSubtaskUpdateAt || 0) + 1;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      saveFiltersToStorage(state.filters);
    },
    clearFilters: (state) => {
      state.filters = { ...DEFAULT_FILTERS };
      clearFiltersFromStorage();
    },
    updateTaskCompletion: (state, action) => {
      const { taskId, completed } = action.payload;
      
      // Helper function to update a task and recalculate parent progress recursively
      const updateTaskInTickets = (tickets, targetTaskId, completed) => {
        return tickets.map(ticket => {
          // If this is the target task, update it
          if (ticket.id === targetTaskId) {
            return {
              ...ticket,
              marked_as_completed: completed
            };
          }
          
          // If this task has subtasks, check them recursively
          if (ticket.subtasks && ticket.subtasks.length > 0) {
            const updatedSubtasks = updateTaskInTickets(ticket.subtasks, targetTaskId, completed);
            
            // Check if any subtask was updated
            const subtaskWasUpdated = updatedSubtasks.some((subtask, index) => 
              subtask.marked_as_completed !== ticket.subtasks[index]?.marked_as_completed
            );
            
            if (subtaskWasUpdated) {
              // Recalculate this parent task's completion based on subtasks
              const completedSubtasks = updatedSubtasks.filter(st => st.marked_as_completed).length;
              const totalSubtasks = updatedSubtasks.length;
              const allSubtasksCompleted = completedSubtasks === totalSubtasks;
              
              return {
                ...ticket,
                subtasks: updatedSubtasks,
                marked_as_completed: allSubtasksCompleted
              };
            }
          }
          
          return ticket;
        });
      };
      
      // Update completion status in all tickets using recursive approach
      Object.keys(state.tickets).forEach(statusName => {
        if (state.tickets[statusName] && Array.isArray(state.tickets[statusName].tickets)) {
          state.tickets[statusName].tickets = updateTaskInTickets(
            state.tickets[statusName].tickets, 
            taskId, 
            completed
          );
        }
      });
    },
    updateTicketAssignee: (state, action) => {
      const { ticketId, assignees } = action.payload;
      // assignees: array of { id, name } objects
      const safeAssignees = Array.isArray(assignees) ? assignees : [];
      const primaryName = safeAssignees[0]?.name || '';
      Object.keys(state.tickets).forEach(statusName => {
        if (state.tickets[statusName] && Array.isArray(state.tickets[statusName].tickets)) {
          const ticketIndex = state.tickets[statusName].tickets.findIndex(t => isSameTicketId(t.id, ticketId));
          if (ticketIndex !== -1) {
            state.tickets[statusName].tickets[ticketIndex].assignee = primaryName;
            state.tickets[statusName].tickets[ticketIndex].assignees = safeAssignees;
          }
        }
      });
      if (state.ticketDetails && isSameTicketId(state.ticketDetails.id, ticketId)) {
        state.ticketDetails.users = safeAssignees;
      }
    },
    updateTicketStatus: (state, action) => {
      const { ticketId, oldStatus, newStatus } = action.payload;
      const oldStatusName = oldStatus?.name;
      const newStatusName = newStatus?.name;
      let sourceStatusName = oldStatusName && state.tickets[oldStatusName] ? oldStatusName : null;

      if (!sourceStatusName) {
        sourceStatusName = Object.keys(state.tickets).find((statusName) =>
          Array.isArray(state.tickets[statusName]?.tickets) &&
          state.tickets[statusName].tickets.some((ticket) => isSameTicketId(ticket.id, ticketId))
        );
      }

      // Move ticket from old status to new status
      if (sourceStatusName && newStatusName && sourceStatusName !== newStatusName &&
          Array.isArray(state.tickets[sourceStatusName]?.tickets)) {
        const ticket = state.tickets[sourceStatusName].tickets.find(t => isSameTicketId(t.id, ticketId));
        if (ticket) {
          if (!state.tickets[newStatusName]) {
            state.tickets[newStatusName] = { tickets: [], total: 0 };
          }
          if (!Array.isArray(state.tickets[newStatusName].tickets)) {
            state.tickets[newStatusName].tickets = [];
          }

          // Remove from old status using filter
          state.tickets[sourceStatusName].tickets = state.tickets[sourceStatusName].tickets.filter(t => !isSameTicketId(t.id, ticketId));
          state.tickets[sourceStatusName].total = Math.max(0, (state.tickets[sourceStatusName].total || 1) - 1);
          // Add to new status
          state.tickets[newStatusName].tickets.unshift({
            ...ticket,
            status: newStatus,
            status_id: newStatus?.id
          });
          state.tickets[newStatusName].total = (state.tickets[newStatusName].total || 0) + 1;
        }
      }
      // Update ticket details if it's the same ticket
      if (state.ticketDetails && isSameTicketId(state.ticketDetails.id, ticketId)) {
        state.ticketDetails.status = newStatus;
      }
    },
    updateTicketDueDate: (state, action) => {
      const { ticketId, dueDate } = action.payload;
      // Update due date in all tickets using indexed approach
      Object.keys(state.tickets).forEach(statusName => {
        if (state.tickets[statusName] && Array.isArray(state.tickets[statusName].tickets)) {
          const ticketIndex = state.tickets[statusName].tickets.findIndex(t => isSameTicketId(t.id, ticketId));
          if (ticketIndex !== -1) {
            state.tickets[statusName].tickets[ticketIndex].due_date = dueDate;
          }
        }
      });
      // Update ticket details if it's the same ticket
      if (state.ticketDetails && isSameTicketId(state.ticketDetails.id, ticketId)) {
        state.ticketDetails.end_date = dueDate;
      }
    },
    updateTicketTitle: (state, action) => {
      const { ticketId, title } = action.payload;
      // Update title in all tickets using indexed approach
      Object.keys(state.tickets).forEach(statusName => {
        if (state.tickets[statusName] && Array.isArray(state.tickets[statusName].tickets)) {
          const ticketIndex = state.tickets[statusName].tickets.findIndex(t => isSameTicketId(t.id, ticketId));
          if (ticketIndex !== -1) {
            state.tickets[statusName].tickets[ticketIndex].title = title;
          }
        }
      });
      // Update ticket details if it's the same ticket
      if (state.ticketDetails && isSameTicketId(state.ticketDetails.id, ticketId)) {
        state.ticketDetails.title = title;
      }
    },
    optimisticMergeTicket: (state, action) => {
      const { ticketId, updates = {} } = action.payload || {};
      const mergeData = buildTicketMergeData({}, updates);
      applyTicketMergeToState(state, ticketId, mergeData);
    },
    setProjectDropdownStatus: (state, action) => {
      const { projectId, status } = action.payload;
      state.projectDropdownsByUser = state.projectDropdownsByUser.map(project =>
        String(project.id) === String(projectId)
          ? { ...project, status }
          : project
      );
    },
    initializeColumnPagination: (state, action) => {
      const { statusId, hasMore, page, loading } = action.payload;
      if (!state.columnPagination[statusId]) {
        state.columnPagination[statusId] = { hasMore, page, loading };
      }
    },
    setDraggedStatus: (state, action) => {
      state.draggedStatus = action.payload;
    },
    setDragOverStatus: (state, action) => {
      state.dragOverStatus = action.payload;
    },
    reorderStatuses: (state, action) => {
      const { statuses, previousOrder } = action.payload;
      state.dropdowns.statuses = statuses;
      if (previousOrder) {
        state.previousStatusOrder = previousOrder;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTicketDropdowns.pending, (state) => {
        state.dropdownsLoading = true;
        state.error = null;
      })
      .addCase(fetchTicketDropdowns.fulfilled, (state, action) => {
        state.dropdownsLoading = false;
        const data = action.payload || {};
        state.dropdowns = {
          types: Array.isArray(data.type) ? data.type : [],
          priorities: sortPriorities(Array.isArray(data.priority) ? data.priority : []),
          statuses: Array.isArray(data.status) ? data.status : [],
          projects: Array.isArray(data.projects) ? data.projects : [],
          assignees: Array.isArray(data.assignees) ? data.assignees : [],
          labels: Array.isArray(data.labels) ? data.labels : [],
          sprints: Array.isArray(data.sprint) ? data.sprint : [],
          scrum_sprints: Array.isArray(data.scrum_sprints) ? data.scrum_sprints : [],
          relationship_types: Array.isArray(data.relationship_types) ? data.relationship_types : [],
          epics: Array.isArray(data.epics) ? data.epics : [],
          sprint_priorities: sortPriorities(Array.isArray(data.sprint_priority) ? data.sprint_priority : [])
        };
      })
      .addCase(fetchTicketDropdowns.rejected, (state, action) => {
        state.dropdownsLoading = false;
        state.error = action.error.message;
      })
      .addCase(fetchProjectDropdownsByUser.pending, (state) => {
        state.projectDropdownsByUserLoading = true;
        state.projectDropdownsByUserError = null;
      })
      .addCase(fetchProjectDropdownsByUser.fulfilled, (state, action) => {
        state.projectDropdownsByUserLoading = false;
        state.projectDropdownsByUserError = null;
        state.projectDropdownsByUser = action.payload;
      })
      .addCase(fetchProjectDropdownsByUser.rejected, (state, action) => {
        state.projectDropdownsByUserLoading = false;
        state.projectDropdownsByUserError = action.payload ?? action.error?.message ?? 'Unable to load projects';
        state.error = action.payload ?? action.error?.message ?? 'Unable to load projects';
      })
      .addCase(fetchLastSelectedProject.fulfilled, (state, action) => {
        state.lastSelectedProjectId = action.payload;
      })
      .addCase(saveUserPreference.fulfilled, (state, action) => {
        if (action.meta?.arg?.preferenceKey === 'last_selected_project') {
          state.lastSelectedProjectId = action.meta.arg.preferenceValue.project_id;
        }
      })
      .addCase(createTicket.fulfilled, (state, action) => {
        state.lastCreated = action.payload;
      })
      .addCase(createTicket.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(updateTicket.fulfilled, (state, action) => {
        const updated = action.payload || {};
        const argData = action.meta?.arg?.ticketData || {};
        const mergeData = buildTicketMergeData(updated, argData);
        const updatedId = mergeData.id ?? updated.id ?? action.meta?.arg?.ticketId;
        applyTicketMergeToState(state, updatedId, mergeData);
      })
      .addCase(updateTicket.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(updateTicketPartial.fulfilled, (state, action) => {
        const updated = action.payload || {};
        const argData = action.meta?.arg?.ticketData || {};
        const mergeData = buildTicketMergeData(updated, argData);
        const updatedId = mergeData.id ?? updated.id ?? action.meta?.arg?.ticketId;
        applyTicketMergeToState(state, updatedId, mergeData);
      })
      .addCase(updateTicketPartial.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(updateTicketAttachments.fulfilled, (state, action) => {
        const updated = action.payload || {};
        applyTicketMergeToState(state, updated.id ?? action.meta?.arg?.ticketId, updated);
      })
      .addCase(updateTicketAttachments.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(deleteTicket.fulfilled, (state, action) => {
        // Ticket deleted successfully
      })
      .addCase(deleteTicket.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchTicketComments.pending, (state) => {
        state.commentsLoading = true;
        state.error = null;
      })
      .addCase(fetchTicketComments.fulfilled, (state, action) => {
        state.commentsLoading = false;
        state.comments = action.payload;
      })
      .addCase(fetchTicketComments.rejected, (state, action) => {
        state.commentsLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(createTicketComment.fulfilled, (state, action) => {
        // Comment created successfully
      })
      .addCase(createTicketComment.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(updateComment.fulfilled, (state, action) => {
        // Comment updated successfully
      })
      .addCase(updateComment.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        // Comment deleted successfully
      })
      .addCase(deleteComment.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(createWorklog.fulfilled, (state, action) => {
        // Worklog created successfully
      })
      .addCase(createWorklog.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(updateWorklog.fulfilled, (state, action) => {
        // Worklog updated successfully
      })
      .addCase(updateWorklog.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(deleteWorklog.fulfilled, (state, action) => {
        // Worklog deleted successfully
      })
      .addCase(deleteWorklog.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchWorklogSummary.pending, (state) => {
        state.worklogSummaryLoading = true;
        state.error = null;
      })
      .addCase(fetchWorklogSummary.fulfilled, (state, action) => {
        state.worklogSummaryLoading = false;
        state.worklogSummary = action.payload;
      })
      .addCase(fetchWorklogSummary.rejected, (state, action) => {
        state.worklogSummaryLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchTicketHistory.pending, (state) => {
        state.ticketHistoryLoading = true;
        state.error = null;
      })
      .addCase(fetchTicketHistory.fulfilled, (state, action) => {
        state.ticketHistoryLoading = false;
        state.ticketHistory = action.payload;
      })
      .addCase(fetchTicketHistory.rejected, (state, action) => {
        state.ticketHistoryLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchTicketIdByNumber.pending, (state) => {
        state.ticketIdByNumberLoading = true;
        state.error = null;
      })
      .addCase(fetchTicketIdByNumber.fulfilled, (state, action) => {
        state.ticketIdByNumberLoading = false;
        state.ticketIdByNumber = action.payload;
      })
      .addCase(fetchTicketIdByNumber.rejected, (state, action) => {
        state.ticketIdByNumberLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchTicketDetails.pending, (state) => {
        state.ticketDetailsLoading = true;
        state.error = null;
      })
      .addCase(fetchTicketDetails.fulfilled, (state, action) => {
        state.ticketDetailsLoading = false;
        state.ticketDetails = action.payload;
      })
      .addCase(fetchTicketDetails.rejected, (state, action) => {
        state.ticketDetailsLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(createTicketType.fulfilled, (state, action) => {
        const created = action.payload;
        if (created) {
          state.lastCreated = created;
          // Optimistically append if shape matches
          state.dropdowns.types = [{ id: created.id, name: created.name, status: created.status }, ...state.dropdowns.types];
        }
      })
      .addCase(createTicketType.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(createTicketPriority.fulfilled, (state, action) => {
        const created = action.payload;
        if (created && state.dropdowns) {
          state.dropdowns.priorities = sortPriorities([{ id: created.id, name: created.name, colour: created.colour, status: created.status }, ...state.dropdowns.priorities]);
        }
      })
      .addCase(createSprintPriority.fulfilled, (state, action) => {
        const created = action.payload;
        if (created && state.dropdowns) {
          state.dropdowns.sprint_priorities = sortPriorities([{ id: created.id, name: created.name, colour: created.colour, status: created.status }, ...(state.dropdowns.sprint_priorities || [])]);
        }
      })
      .addCase(createTicketPriority.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(createTicketLabel.fulfilled, (state, action) => {
        const created = action.payload;
        if (created) {
          state.lastCreated = created;
          state.dropdowns.labels = [{ id: created.id, name: created.name, colour: created.colour, status: created.status }, ...state.dropdowns.labels];
        }
      })
      .addCase(createTicketLabel.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(createTicketSprint.fulfilled, (state, action) => {
        const created = action.payload;
        if (created) {
          state.lastCreated = created;
          state.dropdowns.sprints = [{ id: created.id, name: created.name, colour: created.colour, status: created.status }, ...state.dropdowns.sprints];
        }
      })
      .addCase(createTicketSprint.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(createTicketStatus.fulfilled, (state, action) => {
        const created = action.payload;
        if (created) {
          state.lastCreated = created;
          state.dropdowns.statuses = [{ id: created.id, name: created.name, colour: created.colour, status: created.status }, ...state.dropdowns.statuses];
        }
      })
      .addCase(createTicketStatus.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(updateTicketDropdown.fulfilled, (state, action) => {
        const { id, name, category, colour } = action.payload;
        if (category === TICKET_CATEGORIES.TYPE) {
          const index = state.dropdowns.types.findIndex(item => item.id === id);
          if (index !== -1) state.dropdowns.types[index].name = name;
        } else if (category === TICKET_CATEGORIES.PRIORITY) {
          const index = state.dropdowns.priorities.findIndex(item => item.id === id);
          if (index !== -1) {
            state.dropdowns.priorities[index].name = name;
            if (colour) state.dropdowns.priorities[index].colour = colour;
            state.dropdowns.priorities = sortPriorities(state.dropdowns.priorities);
          }
        } else if (category === 'sprint_priority') {
          const index = (state.dropdowns.sprint_priorities || []).findIndex(item => item.id === id);
          if (index !== -1) {
            state.dropdowns.sprint_priorities[index].name = name;
            if (colour) state.dropdowns.sprint_priorities[index].colour = colour;
            state.dropdowns.sprint_priorities = sortPriorities(state.dropdowns.sprint_priorities);
          }
        } else if (category === TICKET_CATEGORIES.STATUS) {
          const index = state.dropdowns.statuses.findIndex(item => item.id === id);
          if (index !== -1) {
            state.dropdowns.statuses[index].name = name;
            if (colour) state.dropdowns.statuses[index].colour = colour;
          }
        } else if (category === TICKET_CATEGORIES.LABELS) {
          const index = state.dropdowns.labels.findIndex(item => item.id === id);
          if (index !== -1) {
            state.dropdowns.labels[index].name = name;
            if (colour) state.dropdowns.labels[index].colour = colour;
          }
        } else if (category === TICKET_CATEGORIES.SPRINT) {
          const index = state.dropdowns.sprints.findIndex(item => item.id === id);
          if (index !== -1) {
            state.dropdowns.sprints[index].name = name;
            if (colour) state.dropdowns.sprints[index].colour = colour;
          }
        }
      })
      .addCase(deleteTicketDropdown.fulfilled, (state, action) => {
        const { id, category } = action.payload;
        if (category === TICKET_CATEGORIES.TYPE) {
          state.dropdowns.types = state.dropdowns.types.filter(item => item.id !== id);
          if (state.filters.selectedTaskType === id) {
            state.filters.selectedTaskType = FILTER_VALUES.ALL;
          }
        } else if (category === TICKET_CATEGORIES.PRIORITY) {
          state.dropdowns.priorities = state.dropdowns.priorities.filter(item => item.id !== id);
          if (state.filters.selectedPriority === id) {
            state.filters.selectedPriority = FILTER_VALUES.ALL;
          }
        } else if (category === 'sprint_priority') {
          state.dropdowns.sprint_priorities = (state.dropdowns.sprint_priorities || []).filter(item => item.id !== id);
          if (state.filters.selectedPriority === id) {
            state.filters.selectedPriority = FILTER_VALUES.ALL;
          }
        } else if (category === TICKET_CATEGORIES.STATUS) {
          state.dropdowns.statuses = state.dropdowns.statuses.filter(item => item.id !== id);
          if (state.filters.selectedStatus === id) {
            state.filters.selectedStatus = FILTER_VALUES.ALL;
          }
        } else if (category === TICKET_CATEGORIES.LABELS) {
          state.dropdowns.labels = state.dropdowns.labels.filter(item => item.id !== id);
          if (state.filters.selectedLabels && state.filters.selectedLabels.includes(id)) {
            state.filters.selectedLabels = state.filters.selectedLabels.filter(labelId => labelId !== id);
            if (state.filters.selectedLabels.length === 0) {
              state.filters.selectedLabels = [FILTER_VALUES.ALL];
            }
          }
        } else if (category === TICKET_CATEGORIES.SPRINT) {
          state.dropdowns.sprints = state.dropdowns.sprints.filter(item => item.id !== id);
          if (state.filters.selectedSprint && state.filters.selectedSprint.includes(id)) {
            state.filters.selectedSprint = state.filters.selectedSprint.filter(sprintId => sprintId !== id);
            if (state.filters.selectedSprint.length === 0) {
              state.filters.selectedSprint = [FILTER_VALUES.ALL];
            }
          }
        }
      })
      .addCase(fetchAllTickets.pending, (state) => {
        state.ticketsLoading = true;
        state.error = null;
      })
      .addCase(fetchAllTickets.fulfilled, (state, action) => {
        state.ticketsLoading = false;
        state.allTickets = action.payload;
      })
      .addCase(fetchAllTickets.rejected, (state, action) => {
        state.ticketsLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchTicketsByProject.pending, (state, action) => {
        state.ticketsLoading = true;
        state.error = null;
        state.latestTicketsRequestId = action.meta.requestId;
        if (!action.meta.arg?.append) {
          state.tickets = {};
          state.columnPagination = {};
        }
      })
      .addCase(fetchTicketsByProject.fulfilled, (state, action) => {
        if (state.latestTicketsRequestId && state.latestTicketsRequestId !== action.meta.requestId) {
          return;
        }
        state.ticketsLoading = false;
        const { data, append, page } = action.payload;
        
        if (append) {
          // Merge new tickets with existing ones
          Object.keys(data).forEach(statusName => {
            if (state.tickets[statusName]) {
              const existingTickets = state.tickets[statusName].tickets || [];
              const newTickets = data[statusName]?.tickets || [];
              // Ensure we don't add duplicate tickets
              const existingIds = new Set(existingTickets.map(t => t.id));
              const uniqueNewTickets = newTickets.filter(t => !existingIds.has(t.id));
              
              state.tickets[statusName] = {
                ...state.tickets[statusName],
                tickets: [...existingTickets, ...uniqueNewTickets],
                total: data[statusName]?.total || state.tickets[statusName].total
              };
            } else {
              state.tickets[statusName] = data[statusName];
            }
          });
        } else {
          state.tickets = data;
          state.currentPage = PAGINATION.DEFAULT_PAGE;
          // Reset column pagination when new data is loaded (filters changed)
          state.columnPagination = {};
        }
        
        state.currentPage = page;
        
        // Check if there are more tickets to load based on current page data
        const currentPageTickets = Object.values(data).reduce((sum, status) => sum + (status.tickets?.length || 0), 0);
        const totalAvailable = Math.max(...Object.values(data).map(status => status.total || 0));
        const currentTotal = Object.values(state.tickets).reduce((sum, status) => sum + (status.tickets?.length || 0), 0);
        
        state.hasMoreTickets = currentTotal < totalAvailable && currentPageTickets > 0;
      })
      .addCase(fetchTicketsByProject.rejected, (state, action) => {
        if (state.latestTicketsRequestId && state.latestTicketsRequestId !== action.meta.requestId) {
          return;
        }
        state.ticketsLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchTicketsByColumn.pending, (state, action) => {
        const { statusId } = action.meta.arg;
        if (!state.columnPagination[statusId]) {
          state.columnPagination[statusId] = { page: PAGINATION.DEFAULT_PAGE, hasMore: true, loading: false };
        }
        state.columnPagination[statusId].loading = true;
        state.error = null;
      })
      .addCase(fetchTicketsByColumn.fulfilled, (state, action) => {
        const { data, statusId, page } = action.payload;
        
        // Initialize column pagination if not exists
        if (!state.columnPagination[statusId]) {
          state.columnPagination[statusId] = { page: PAGINATION.DEFAULT_PAGE, hasMore: true, loading: false };
        }
        
        // Find status name from statusId
        const status = state.dropdowns.statuses.find(s => s.id === statusId);
        const statusName = status?.name;
        
        if (statusName && data[statusName]) {
          const statusData = data[statusName];
          
          if (state.tickets[statusName]) {
            // Append new tickets to existing ones
            const existingTickets = state.tickets[statusName].tickets || [];
            const newTickets = statusData.tickets || [];
            const existingIds = new Set(existingTickets.map(t => t.id));
            const uniqueNewTickets = newTickets.filter(t => !existingIds.has(t.id));
            
            // Use spread operators to avoid mutation
            state.tickets = {
              ...state.tickets,
              [statusName]: {
                ...state.tickets[statusName],
                tickets: [...existingTickets, ...uniqueNewTickets],
                total: statusData.total || state.tickets[statusName].total
              }
            };
          } else {
            state.tickets[statusName] = statusData;
          }
          
          // Update column pagination
          state.columnPagination[statusId].page = page;
          state.columnPagination[statusId].loading = false;
          
          // Check if there are more tickets for this column
          const currentTickets = state.tickets[statusName].tickets?.length || 0;
          const totalTickets = statusData.total || 0;
          const newTicketsCount = statusData.tickets?.length || 0;
          const hasMore = currentTickets < totalTickets && newTicketsCount > 0;
          
          state.columnPagination[statusId].hasMore = hasMore;
        } else {
          state.columnPagination[statusId].loading = false;
          state.columnPagination[statusId].hasMore = false;
        }
      })
      .addCase(fetchTicketsByColumn.rejected, (state, action) => {
        const { statusId } = action.meta.arg;
        if (state.columnPagination[statusId]) {
          state.columnPagination[statusId].loading = false;
        }
        state.error = action.payload || action.error.message;
      })
      // Clear filters when logout action is dispatched
      .addCase('auth/logout', (state) => {
        state.filters = { ...DEFAULT_FILTERS };
        clearFiltersFromStorage();
        state.tickets = {};
        state.columnPagination = {};
      })
      // Prevent clearing tickets on route changes
      .addCase('@@router/LOCATION_CHANGE', (state) => {
        // Do not clear tickets on navigation
      })
      .addCase(updateStatusOrder.fulfilled, (state, action) => {
        const { statusOrder } = action.payload;
        if (state.dropdowns?.statuses && statusOrder) {
          // Sort statuses according to server-confirmed order
          state.dropdowns.statuses.sort((a, b) => statusOrder.indexOf(a.id) - statusOrder.indexOf(b.id));
        }
        state.previousStatusOrder = null;
        state.error = null;
      })
      .addCase(updateStatusOrder.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
        // Rollback to previous order if available
        if (state.previousStatusOrder) {
          state.dropdowns.statuses = state.previousStatusOrder;
          state.previousStatusOrder = null;
        }
      })
      .addCase(bulkUpdateTickets.fulfilled, (state, action) => {
        // Bulk update successful
      })
      .addCase(bulkUpdateTickets.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(bulkDeleteTickets.fulfilled, (state, action) => {
        // Bulk delete successful
      })
      .addCase(bulkDeleteTickets.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(linkTickets.fulfilled, (state, action) => {
        // Tickets linked successfully
        state.lastLinkUpdateAt = (state.lastLinkUpdateAt || 0) + 1;
      })
      .addCase(linkTickets.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(updateLinkTickets.fulfilled, (state, action) => {
        // Link updated successfully
        state.lastLinkUpdateAt = (state.lastLinkUpdateAt || 0) + 1;
      })
      .addCase(updateLinkTickets.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(deleteLinkTickets.fulfilled, (state, action) => {
        // Link deleted successfully - no local state update needed as components will refresh data
        state.lastLinkUpdateAt = (state.lastLinkUpdateAt || 0) + 1;
      })
      .addCase(deleteLinkTickets.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(searchTickets.pending, (state) => {
        state.searchLoading = true;
        state.error = null;
      })
      .addCase(searchTickets.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload;
      })
      .addCase(searchTickets.rejected, (state, action) => {
        state.searchLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(searchSprints.pending, (state) => {
        state.searchSprintsLoading = true;
        state.error = null;
      })
      .addCase(searchSprints.fulfilled, (state, action) => {
        state.searchSprintsLoading = false;
        state.searchSprintsResults = action.payload;
      })
      .addCase(searchSprints.rejected, (state, action) => {
        state.searchSprintsLoading = false;
        state.error = action.payload || action.error.message;
      })
      // Listen to updateProject from projectSlice for optimistic updates
      .addCase(updateProject.fulfilled, (state, action) => {
        // Get project ID from the action meta (original arguments)
        const projectId = action.meta?.arg?.id;
        const updatedStatus = action.meta?.arg?.data?.status;
        
        if (projectId && updatedStatus) {
          // Optimistically update projectDropdownsByUser
          state.projectDropdownsByUser = state.projectDropdownsByUser.map(project => 
            project.id === projectId 
              ? { ...project, status: updatedStatus }
              : project
          );
        }
      });
  }
});

// Selectors
export const getDraggedStatus = (state) => state.tickets.draggedStatus;
export const getDragOverStatus = (state) => state.tickets.dragOverStatus;
export const getDropdowns = (state) => state.tickets.dropdowns;
export const getTickets = (state) => state.tickets.tickets;
export const getColumnPagination = (state) => state.tickets.columnPagination;
export const getFilters = (state) => state.tickets.filters;

export const { clearTickets, clearTicketDetails, clearError, setFilters, clearFilters, updateTaskCompletion, updateTicketAssignee, updateTicketStatus, updateTicketDueDate, updateTicketTitle, optimisticMergeTicket, setProjectDropdownStatus, initializeColumnPagination, setDraggedStatus, setDragOverStatus, reorderStatuses, markSubtaskUpdated } = ticketSlice.actions;
export default ticketSlice.reducer;
